<template>
  <div class="flex flex-col items-start w-full">
    <!-- Hidden on mobile -->
    <ControlCenterChangeInvestment is-finalize-step class="hidden lg:block" />

    <PaperCard class="lg:mt-8 py-8 w-full" :has_header="false">
      <template #body>
        <div class="flex items-end justify-center gap-4 pt-6 max-w-[600px] mx-auto">
          <div
            v-for="card in displayCards"
            :key="card.key"
            class="flex flex-col min-w-[168.5px] md:min-w-[250px] w-full md:w-auto rounded-xl"
            :class="[
              outerCardStyles(card.tier),
              cardHeight(card.tier),
              card.tier === 'better' ? 'hidden lg:flex' : 'flex',
            ]"
          >
            <UpsellCard
              :card="card"
              :base-principal="basePrincipal"
              :compounded="compounded"
              @select="onSelect"
            />
          </div>
        </div>
      </template>
    </PaperCard>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { PaperCard } from "@/components";
import { useDataStore, usePurchaseFlowStore } from "@/stores";
import ControlCenterChangeInvestment from "./PurchaseFlowUtilities/ControlCenterChangeInvestment.vue";
import UpsellCard from "./PurchaseFlowUtilities/UpsellCard.vue";

const dataStore = useDataStore();
const purchaseFlowStore = usePurchaseFlowStore();

const basePrincipal = computed(() => {
  const amount = purchaseFlowStore.current_investment.amount ?? "0";
  const val = typeof amount === "string" ? parseInt(amount.replace(/,/g, "")) : amount;
  return isNaN(val) ? 0 : val;
});

const compounded = computed(() => !!purchaseFlowStore.current_investment.is_compounded);

const currentOffering = computed(() => purchaseFlowStore.selected_offering);

const sortedOfferings = computed(() => {
  if (!currentOffering.value) return [];
  return [...(dataStore.offerings || [])]
    .filter((o) =>
      currentOffering.value.type === "Reg D"
        ? o.type === currentOffering.value.type && o.tier === currentOffering.value.tier
        : o.type === currentOffering.value.type
    )
    .sort((a, b) => a.bond_term - b.bond_term);
});

const displayCards = computed(() => {
  if (!currentOffering.value || sortedOfferings.value.length === 0) return [];

  const currentIndex = sortedOfferings.value.findIndex(
    (o) => o.record_id === currentOffering.value.record_id
  );
  if (currentIndex === -1) return [];

  const higherOfferings = sortedOfferings.value.slice(currentIndex + 1);
  const principal = basePrincipal.value;

  // Already on highest term — upsell via amount increases
  if (higherOfferings.length === 0) {
    return [
      {
        key: `${currentOffering.value.record_id}-current`,
        offering: currentOffering.value,
        tier: "current",
        principal,
      },
      {
        key: `${currentOffering.value.record_id}-better`,
        offering: currentOffering.value,
        tier: "better",
        principal: principal + 25000,
      },
      {
        key: `${currentOffering.value.record_id}-best`,
        offering: currentOffering.value,
        tier: "best",
        principal: principal + 50000,
      },
    ];
  }

  // Second-highest term — "better" is next term, "best" is that term + $25k
  if (higherOfferings.length === 1) {
    return [
      {
        key: `${currentOffering.value.record_id}-current`,
        offering: currentOffering.value,
        tier: "current",
        principal,
      },
      {
        key: `${higherOfferings[0].record_id}-better`,
        offering: higherOfferings[0],
        tier: "better",
        principal,
      },
      {
        key: `${higherOfferings[0].record_id}-best`,
        offering: higherOfferings[0],
        tier: "best",
        principal: principal + 25000,
      },
    ];
  }

  // Normal — upsell via longer terms
  return [
    {
      key: `${currentOffering.value.record_id}-current`,
      offering: currentOffering.value,
      tier: "current",
      principal,
    },
    {
      key: `${higherOfferings[0].record_id}-better`,
      offering: higherOfferings[0],
      tier: "better",
      principal,
    },
    {
      key: `${higherOfferings[higherOfferings.length - 1].record_id}-best`,
      offering: higherOfferings[higherOfferings.length - 1],
      tier: "best",
      principal,
    },
  ];
});

function outerCardStyles(tier) {
  switch (tier) {
    case "best":
      return "bg-green-800";
    case "better":
      return "bg-container-outline border border-container-outline";
    default:
      return "border border-container-outline bg-transparent";
  }
}

function cardHeight(tier) {
  switch (tier) {
    case "best":
      return "min-h-[320px]";
    case "better":
      return "min-h-[280px]";
    default:
      return "min-h-[240px]";
  }
}

function onSelect(card) {
  purchaseFlowStore.selected_offering = card.offering;
  if (card.principal !== basePrincipal.value) {
    purchaseFlowStore.current_investment.amount = card.principal.toString();
  }
}
</script>
